import { useState, useEffect } from 'react';
import SortableTree from '@nosferatu500/react-sortable-tree';
import FileExplorerTheme from '@nosferatu500/theme-file-explorer';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';

import { actionButton } from './NodeButton';
import { updateTitle, updateIcon, getNodeKey, emptyTree, useCurrentTypeSlug } from './helper';
import './tree.scss';
import { PropTypes } from 'prop-types';

const NavTree = ({ treeData, updateTreeData, updateNewTypeNode, resetTablePagination, archivedTypeFn, isRootAdmin, drawerToggle }) => {
    const currentType = useCurrentTypeSlug();
    const [navTreeData, setNavTreeData] = useState(treeData || emptyTree());
    const [searchString, setSearchString] = useState('');
    const [searchFocusIndex, setSearchFocusIndex] = useState(0);
    const [searchFoundCount, setSearchFoundCount] = useState(0);

    useEffect(() => {
        setNavTreeData(treeData);
    }, [treeData]);

    const updateFolderName = (treeData, isRequestForUpdate) => {
        setNavTreeData(treeData);
        if (isRequestForUpdate) {
            updateTreeData(treeData, isRequestForUpdate);
        }
    };

    const updateTreeDataFn = (treeData, isRequestForUpdate, isRequestForMoveNode) => {
        setNavTreeData(treeData);
        if (isRequestForUpdate) {
            updateTreeData(treeData, isRequestForUpdate, isRequestForMoveNode);
        }
    };

    const updateNewTypeNodeFn = (data) => {
        updateNewTypeNode(data);
    };

    const customSearchMethod = ({ node, searchQuery }) =>
        !!searchQuery && typeof node.title === 'string' && node.title.toLowerCase().indexOf(searchQuery.toLowerCase()) > -1;

    const clearSearch = () => {
        setSearchString('');
        setSearchFocusIndex(0);
    };

    const selectPrevMatch = () => {
        if (!searchFoundCount) return;
        setSearchFocusIndex((searchFoundCount + searchFocusIndex - 1) % searchFoundCount);
    };

    const selectNextMatch = () => {
        if (!searchFoundCount) return;
        setSearchFocusIndex((searchFocusIndex + 1) % searchFoundCount);
    };

    return (
        <div className="nav_tree">
            <div className="nav_search_wrap">
                <i>
                    <SearchOutlinedIcon />
                </i>
                <input
                    className={searchString ? 'nav_search nav_search--searching' : 'nav_search'}
                    type="text"
                    placeholder="Search Configurations"
                    value={searchString}
                    onChange={(ev) => {
                        setSearchString(ev.target.value);
                        setSearchFocusIndex(0);
                    }}
                />
                {searchString && (
                    <div className="nav_search_nav">
                        <span className="nav_search_count">{searchFoundCount ? `${searchFocusIndex + 1}/${searchFoundCount}` : '0/0'}</span>
                        <button type="button" aria-label="Previous match" disabled={!searchFoundCount} onClick={selectPrevMatch}>
                            <KeyboardArrowUpIcon />
                        </button>
                        <button type="button" aria-label="Next match" disabled={!searchFoundCount} onClick={selectNextMatch}>
                            <KeyboardArrowDownIcon />
                        </button>
                        <button type="button" aria-label="Clear search" onClick={clearSearch}>
                            <CloseIcon />
                        </button>
                    </div>
                )}
            </div>
            <div className="nav_tree_scroll">
                <SortableTree
                    rowHeight={45}
                    theme={FileExplorerTheme}
                    treeData={navTreeData}
                    searchQuery={searchString}
                    searchMethod={customSearchMethod}
                    searchFocusOffset={searchFocusIndex}
                    className="sortableTreeClass"
                    canDrag={({ node }) => !node.dragDisabled && isRootAdmin}
                    canDrop={({ nextParent }) => (!nextParent || nextParent.isDirectory) && isRootAdmin}
                    onChange={(data) => updateTreeDataFn(data)}
                    onMoveNode={({ treeData }) => updateTreeDataFn(treeData, true, true)}
                    getNodeKey={getNodeKey}
                    searchFinishCallback={(matches) => {
                        setSearchFoundCount(matches.length);
                        setSearchFocusIndex(matches.length > 0 ? searchFocusIndex % matches.length : 0);
                    }}
                    generateNodeProps={(rowInfo) => ({
                        title: updateTitle(rowInfo, navTreeData, updateTreeDataFn, updateFolderName, resetTablePagination, drawerToggle),
                        icons: updateIcon(rowInfo),
                        buttons: actionButton(rowInfo, navTreeData, updateTreeDataFn, updateNewTypeNodeFn, archivedTypeFn, isRootAdmin),
                        className: !rowInfo.node.isDirectory && rowInfo.node.id === currentType ? 'rst-row--active' : undefined
                    })}
                />
            </div>
        </div>
    );
};

NavTree.propTypes = {
    treeData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    updateTreeData: PropTypes.func.isRequired,
    updateNewTypeNode: PropTypes.func.isRequired,
    resetTablePagination: PropTypes.func,
    archivedTypeFn: PropTypes.func,
    isRootAdmin: PropTypes.bool,
    drawerToggle: PropTypes.func
};

export default NavTree;
